import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Settings, ChevronDown, Building2, Bell, ShieldAlert, CreditCard, Activity } from "lucide-react";
import api from "../utils/api";

const CREDIT_ALERT_TYPES = ["credits_50_percent", "credits_90_percent", "credits_exhausted"];

const Navbar = ({ onToggleSidebar, sidebarCollapsed, activeAiAgentId, setActiveAiAgentId, aiAgents: passedAiAgents, agents, activeChatsCount }) => {
  const aiAgents = passedAiAgents || agents || [];
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [bellPulse, setBellPulse] = useState(false);
  const [creditAlerts, setCreditAlerts] = useState([]);
  // Tracks which credit thresholds have already fired a local alert this session.
  // Cleared when credits drop below the threshold (e.g. after a top-up).
  const shownThresholdsRef = useRef(new Set());

  
  const userStr = sessionStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role === 'admin';

  const activeAiAgent = aiAgents?.find((p) => String(p.id) === String(activeAiAgentId));
  const userTenantId = user?.tenant_id 
    ? String(user.tenant_id) 
    : (activeAiAgent?.tenant_id 
        ? String(activeAiAgent.tenant_id) 
        : (aiAgents?.[0]?.tenant_id 
            ? String(aiAgents[0].tenant_id) 
            : (localStorage.getItem('activeTenantId') || '')
          )
      );


  // ── Live credit check (mirrors the old banner logic) ─────────────────────
  // Generates a local alert exactly ONCE per threshold crossing per session.
  // Uses shownThresholdsRef so repeated 60s polls don't re-trigger a dismissed alert.
  // Resets the flag only when credits drop back below the threshold (e.g. top-up).
  const fetchCreditsAndAlert = useCallback(async () => {
    if (!userTenantId) return;
    try {
      // Enterprise plans skip all credit threshold alerts — only token_spike is allowed
      try {
        const subRes = await api.request(`/api/subscription/${userTenantId}`);
        const planId = subRes?.data?.plan_id || subRes?.data?.planId || '';
        const planName = subRes?.data?.plan_name || subRes?.data?.planName || '';
        if (
          String(planId).toLowerCase() === 'enterprise' ||
          String(planName).toLowerCase() === 'enterprise'
        ) {
          return; // No credit alerts for enterprise plans
        }
      } catch (_subErr) {
        // Subscription check failed — fall through to normal alert logic
      }

      const res = await api.request(`/api/credits/${userTenantId}`);
      if (!res || !res.success || !res.data) return;
      const d = res.data;
      const monthly  = d.monthly_credits  || 0;
      const used     = d.credits_used     || 0;
      const topup    = d.topup_credits    || 0;
      const total    = d.total_credits    || 0;

      const monthlyPct = monthly > 0 ? (used / monthly) * 100 : 0;

      // ── Reset flags when credits drop BELOW a threshold (after top-up) ──
      if (monthlyPct < 50)  shownThresholdsRef.current.delete('50');
      if (monthlyPct < 90)  shownThresholdsRef.current.delete('90');
      if (total > 0)        shownThresholdsRef.current.delete('exhausted');

      const alerts = [];
      const now = new Date().toISOString();

      // Only push an alert if this threshold hasn't been shown yet this session
      if (total <= 0 && (monthly + topup) > 0 && !shownThresholdsRef.current.has('exhausted')) {
        shownThresholdsRef.current.add('exhausted');
        alerts.push({
          id: "__local_exhausted",
          alert_type: "credits_exhausted",
          type: "usage",
          title: "Credits Exhausted",
          message: `Your credits have been completely exhausted. Top up your credits to resume AI agent executions immediately.`,
          is_read: false,
          created_at: now,
          _local: true,
        });
      } else if (monthlyPct >= 90 && !shownThresholdsRef.current.has('90')) {
        shownThresholdsRef.current.add('90');
        alerts.push({
          id: "__local_90",
          alert_type: "credits_90_percent",
          type: "usage",
          title: "Credits Usage Alert — 90% Used",
          message: `You have used ${Math.round(monthlyPct)}% of your monthly credits. ${(monthly - used).toLocaleString()} credits remaining.`,
          is_read: false,
          created_at: now,
          _local: true,
        });
      } else if (monthlyPct >= 50 && !shownThresholdsRef.current.has('50')) {
        shownThresholdsRef.current.add('50');
        alerts.push({
          id: "__local_50",
          alert_type: "credits_50_percent",
          type: "usage",
          title: "Credits Usage Alert — 50% Used",
          message: `You have used ${Math.round(monthlyPct)}% of your monthly credits. ${(monthly - used).toLocaleString()} credits remaining.`,
          is_read: false,
          created_at: now,
          _local: true,
        });
      }

      if (alerts.length > 0) {
        setCreditAlerts(prev => {
          // Avoid duplicates: don't re-add an alert already in the list
          const existingIds = new Set(prev.map(a => a.id));
          const newAlerts = alerts.filter(a => !existingIds.has(a.id));
          return [...prev, ...newAlerts];
        });
        const autoOpened = sessionStorage.getItem("notifications_auto_opened") === "true";
        if (!autoOpened) {
          sessionStorage.setItem("notifications_auto_opened", "true");
          setShowNotifications(true);
        }
      }
    } catch (err) {
      console.error("Failed to fetch credits for notification:", err);
    }
  }, [userTenantId]);

  const fetchNotifications = useCallback(async () => {
    if (!userTenantId) return;
    try {
      const res = await api.get(`/notifications/${userTenantId}`);
      if (res && res.success) {
        const data = res.data || [];
        setNotifications(data);

        // Auto-open the panel once per session when there are unread DB credit alerts
        const hasUnreadCreditAlert = data.some(
          (n) => !n.is_read && CREDIT_ALERT_TYPES.includes(n.alert_type)
        );
        if (hasUnreadCreditAlert) {
          setBellPulse(true);
          const autoOpened = sessionStorage.getItem("notifications_auto_opened") === "true";
          if (!autoOpened) {
            sessionStorage.setItem("notifications_auto_opened", "true");
            setShowNotifications(true);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, [userTenantId]);

  useEffect(() => {
    fetchCreditsAndAlert();
    fetchNotifications();

    const handleCreditsUpdate = () => {
      fetchCreditsAndAlert();
      fetchNotifications();
    };

    window.addEventListener("credits_updated", handleCreditsUpdate);
    window.addEventListener("payment_success", handleCreditsUpdate);

    const interval = setInterval(fetchNotifications, 15000);
    const creditInterval = setInterval(fetchCreditsAndAlert, 60000); // refresh credit state every minute
    return () => {
      clearInterval(interval);
      clearInterval(creditInterval);
      window.removeEventListener("credits_updated", handleCreditsUpdate);
      window.removeEventListener("payment_success", handleCreditsUpdate);
    };
  }, [userTenantId, fetchCreditsAndAlert, fetchNotifications]);

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    return `${diffDays}d ago`;
  };

  const markAsRead = useCallback(async (id) => {
    // Handle local (synthetic) credit alerts
    if (typeof id === "string" && id.startsWith("__local_")) {
      setCreditAlerts(prev => prev.filter(a => a.id !== id));
      const remaining = creditAlerts.filter(a => a.id !== id);
      if (remaining.length === 0) {
        setBellPulse(false);
        sessionStorage.removeItem("notifications_auto_opened");
      }
      return;
    }
    try {
      const res = await api.put(`/notifications/mark-read/${id}`);
      if (res && res.success) {
        setNotifications(prev => {
          const updated = prev.map(n => n.id === id ? { ...n, is_read: true } : n);
          // If no more unread credit alerts anywhere, stop pulsing and allow re-trigger
          const stillHasDbAlert = updated.some(
            n => !n.is_read && CREDIT_ALERT_TYPES.includes(n.alert_type)
          );
          if (!stillHasDbAlert && creditAlerts.length === 0) {
            setBellPulse(false);
            sessionStorage.removeItem("notifications_auto_opened");
          }
          return updated;
        });
      }
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  }, [creditAlerts]);

  const markAllAsRead = useCallback(async () => {
    if (!userTenantId) return;
    try {
      const res = await api.put(`/notifications/mark-all-read/${userTenantId}`);
      if (res && res.success) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        // Also dismiss local credit alerts
        setCreditAlerts([]);
        // Reset pulse & allow re-trigger when new alerts arrive
        setBellPulse(false);
        sessionStorage.removeItem("notifications_auto_opened");
      }
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  }, [userTenantId]);

  // Filter DB notifications to last 7 days
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const dbNotifications = notifications.filter(n => {
    if (!n.created_at) return false;
    return new Date(n.created_at) >= sevenDaysAgo;
  });

  // Merge: local credit alerts at top, then DB notifications.
  // Deduplicate: if DB already has a notification of the same alert_type, skip the local one.
  const dbAlertTypes = new Set(dbNotifications.map(n => n.alert_type));
  const filteredLocalAlerts = creditAlerts.filter(a => !dbAlertTypes.has(a.alert_type));
  const recentNotifications = [...filteredLocalAlerts, ...dbNotifications];

  const getAlertIcon = (alertType) => {
    switch (alertType) {
      case "payment_failed":
      case "credits_exhausted":
      case "daily_spend_exceeded":
      case "token_spike":
        return <ShieldAlert size={16} className="text-red-500" />;
      case "subscription_renewing":
      case "plan_limit_reached":
      case "credits_50_percent":
      case "credits_90_percent":
        return <Activity size={16} className="text-amber-500" />;
      case "invoice_generated":
        return <CreditCard size={16} className="text-emerald-500" />;
      default:
        return <Bell size={16} className="text-gray-500" />;
    }
  };

  const getAlertBg = (alertType) => {
    switch (alertType) {
      case "payment_failed":
      case "credits_exhausted":
      case "daily_spend_exceeded":
      case "token_spike":
        return "bg-red-50";
      case "subscription_renewing":
      case "plan_limit_reached":
      case "credits_50_percent":
      case "credits_90_percent":
        return "bg-amber-50";
      case "invoice_generated":
        return "bg-emerald-50";
      default:
        return "bg-gray-50";
    }
  };

  useEffect(() => {
    if (userTenantId) {
      localStorage.setItem('activeTenantId', userTenantId);
    }
  }, [userTenantId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest(".agent-dropdown")) {
        setIsOpen(false);
      }
      if (showNotifications && !event.target.closest(".notifications-dropdown")) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, showNotifications]);

  const getPageTitle = (location) => {
    const { pathname, search } = location;

    if (pathname.startsWith("/contacts/")) {
      return "Contacts > Profile";
    }

    if (pathname === "/analytics") {
      const params = new URLSearchParams(search);
      const tab = params.get("tab") || "overview";
      const tabName = tab
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      return tabName;
    }

    switch (pathname) {
      case "/chat":
        return "Chat";
      case "/instructions":
        return "AI Agent Instructions";
      case "/models":
        return "Model Management";
      case "/api-tools":
        return "API Tools";
      case "/webhooks":
        return "Incoming Webhooks";
      case "/logo":
        return "Logo Management";
      case "/documents":
        return "Sources > Document Training";
      case "/knowledge-graph":
        return "Sources > Knowledge Graph";
      case "/smart-faq":
        return "Smart FAQ Builder";
      case "/widget":
        return "Widget Integration";
      case "/widget-preview":
        return "Widget Preview";
      case "/domains":
        return "Domain Management";
      case "/integrations":
        return "Integrations";
      case "/telegram-config":
        return "Integrations > Telegram";
      case "/freshchat-config":
        return "Integrations > Freshchat";
      case "/slack-config":
        return "Integrations > Slack";
      case "/email-config":
        return "Integrations > Email";
      case "/whatsapp-config":
        return "Integrations > WhatsApp";
      case "/whatsapp-templates":
        return "Campaigns > WhatsApp Templates";
      case "/zendesk-config":
        return "Integrations > Zendesk";
      case "/freshdesk-config":
        return "Integrations > Freshdesk";
      case "/shopify-config":
        return "Integrations > Shopify";
      case "/ai-agents":
        return "AI Agents";
      case "/evaluations":
        return "Analytics > AI Evaluations";
      case "/segments":
        return "Segments";
      case "/docs":
        return "Help Center";
      default:
        // try to format the path cleanly if unknown
        if (pathname === "/" || !pathname) return "Dashboard";
        return pathname
          .substring(1)
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
    }
  };

  const title = getPageTitle(location);
  
  const assignedAiAgents = user?.assigned_ai_agents || [];

  const visibleAiAgents = isAdmin
    ? aiAgents
    : aiAgents.filter(p => assignedAiAgents.includes(p.id));



  return (
    <nav className="bg-white border-b border-gray-100 px-8 py-3 flex items-center justify-between min-h-[60px] z-[80]">
      <div className="flex items-center gap-4">
        {typeof title === "string" && title.includes(">") ? (
          <div className="flex items-center gap-2 text-xl font-normal tracking-tight">
            {title.split(">").map((part, index, array) => (
              <React.Fragment key={index}>
                {index === 0 ? (
                  <button
                    onClick={() => navigate("/" + part.trim().toLowerCase())}
                    className="text-gray-400 hover:text-gray-900 transition-colors"
                  >
                    {part.trim()}
                  </button>
                ) : (
                  <span className="text-gray-800">{part.trim()}</span>
                )}
                {index < array.length - 1 && (
                  <span className="text-gray-300 font-light">/</span>
                )}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <h1 className="text-xl font-normal text-gray-800 tracking-tight">{title}</h1>
        )}
      </div>
      <div className="flex items-center gap-6">
        {isAdmin || (visibleAiAgents && visibleAiAgents.length > 1) ? (
          <div className="flex items-center gap-3 agent-dropdown relative">
            <span className="text-xs text-gray-400 font-normal uppercase tracking-wider">Active AI Agent</span>
            <div className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-3 px-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-xl hover:bg-white hover:shadow-md hover:border-[#82bbe0]/20 transition-all duration-300 group ${isOpen ? 'ring-2 ring-[#82bbe0]/5 border-[#82bbe0]/40 bg-white shadow-sm' : ''}`}
              >
                <div className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center text-gray-900 group-hover:bg-gray-200 transition-colors">
                  <Building2 size={14} />
                </div>
                <span className="text-sm font-normal text-gray-900 min-w-[120px] text-left">
                  {activeAiAgent?.name || "Select AI Agent"}
                </span>
                <ChevronDown size={16} className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-gray-900' : ''}`} />
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-100 rounded-2xl shadow-xl py-2 z-[9999] animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="px-3 py-1.5 text-[10px] font-normal text-gray-400 uppercase tracking-widest border-b border-gray-50 mb-1">
                    Your AI Agents
                  </div>
                  <div className="max-h-64 overflow-y-auto custom-scrollbar">
                    {visibleAiAgents.map((agent) => (
                      <button
                        key={agent.id}
                        onClick={() => {
                          localStorage.setItem("activeAiAgentId", agent.id);
                          setActiveAiAgentId(agent.id);
                          setIsOpen(false);
                          // Force a hard refresh to reset all component states for the new agent
                          window.location.reload();
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 transition-all hover:bg-gray-50/80 group ${String(activeAiAgentId) === String(agent.id) ? 'bg-gray-100' : ''}`}
                      >
                        <div className={`w-2 h-2 rounded-full transition-all ${String(activeAiAgentId) === String(agent.id) ? 'bg-[#82bbe0] scale-125 shadow-[0_0_8px_rgba(130,187,224,0.2)]' : 'bg-gray-200 group-hover:bg-gray-400'}`}></div>
                        <div className="flex flex-col items-start overflow-hidden">
                          <span className={`text-sm font-semibold truncate uppercase tracking-tight ${String(activeAiAgentId) === String(agent.id) ? 'text-[#82bbe0]' : 'text-gray-600 group-hover:text-gray-900'}`}>
                            {agent.name}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {isAdmin && (
                    <div className="border-t border-gray-50 mt-1 pt-1 px-1">
                      <button
                        onClick={() => {
                          navigate("/ai-agents");
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-[10px] font-normal text-gray-900 uppercase tracking-widest hover:bg-gray-50 transition-all rounded-xl group"
                      >
                        <Settings size={12} className="text-gray-400 group-hover:text-gray-900 transition-colors" />
                        Manage All AI Agents
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : !isAdmin && visibleAiAgents && visibleAiAgents.length === 1 ? (
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 font-normal uppercase tracking-wider">Active Bot</span>
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-100 rounded-xl">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
              <span className="text-sm font-bold text-gray-900">{visibleAiAgents[0].name}</span>
            </div>
          </div>
        ) : null}

        {/* Notification Bell Dropdown */}
        <div className="relative notifications-dropdown">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className={`p-2.5 rounded-xl border border-gray-200 bg-gray-50/50 hover:bg-white hover:shadow-md hover:border-[#82bbe0]/20 text-gray-500 hover:text-gray-900 transition-all duration-300 relative ${showNotifications ? 'ring-2 ring-[#82bbe0]/5 border-[#82bbe0]/40 bg-white shadow-sm' : ''}`}
            aria-label="View Notifications"
          >
            <Bell size={18} className={bellPulse ? "animate-pulse text-[#82bbe0]" : ""} />
            {recentNotifications.some(n => !n.is_read) && (
              <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 border-2 border-white rounded-full"></span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 mt-2 w-96 bg-white border border-gray-100 rounded-2xl shadow-xl py-3 z-50 animate-in fade-in slide-in-from-top-2 duration-200 flex flex-col max-h-[550px]">
              <div className="px-4 pb-2 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">Notifications</span>
                  {recentNotifications.filter(n => !n.is_read).length > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-[#82bbe0]/10 text-[#82bbe0] rounded-full font-medium">
                      {recentNotifications.filter(n => !n.is_read).length} new
                    </span>
                  )}
                </div>
                {recentNotifications.some(n => !n.is_read) && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-[#82bbe0] hover:text-[#5fa2cc] transition-colors font-medium"
                  >
                    Mark all as read
                  </button>
                )}
              </div>

              {/* Notification List */}
              <div className="overflow-y-auto max-h-[400px] flex-grow custom-scrollbar">
                {recentNotifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-400 text-sm flex flex-col items-center justify-center gap-2">
                    <Bell size={24} className="text-gray-300" />
                    <span>No notifications</span>
                  </div>
                ) : (
                  recentNotifications.map(n => (
                      <div
                        key={n.id}
                        onClick={() => {
                          if (!n.is_read) {
                            markAsRead(n.id);
                            
                          }
                          if (n.alert_type === "invoice_generated") {
                            navigate("/billing?tab=invoices");
                            setShowNotifications(false);
                          }
                        }}
                        className={`px-4 py-3 flex gap-3 transition-colors hover:bg-gray-50/50 cursor-pointer ${
                          !n.is_read ? 'bg-[#82bbe0]/5' : ''
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getAlertBg(n.alert_type)}`}>
                          {getAlertIcon(n.alert_type)}
                        </div>
                        <div className="flex-grow min-w-0">
                           <div className="flex items-start justify-between gap-2">
                            <span className={`text-xs truncate ${
                              !n.is_read ? 'font-bold text-[#82bbe0]' : 'font-semibold text-gray-700'
                            }`}>
                              {n.title}
                            </span>
                            <span className={`text-[10px] whitespace-nowrap ${
                              !n.is_read ? 'text-[#82bbe0]/80' : 'text-gray-400'
                            }`}>
                              {formatTimeAgo(n.created_at)}
                            </span>
                          </div>
                          <p className={`text-xs mt-0.5 leading-relaxed break-words ${
                            !n.is_read ? 'text-[#82bbe0] font-semibold' : 'text-gray-500'
                          }`}>
                            {n.message}
                          </p>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sign Out button for non-admin human agents (whose sidebar is hidden) */}
        {!isAdmin && (
          <button
            onClick={() => {
              localStorage.removeItem("user");
              localStorage.removeItem("activeAiAgentId");
              localStorage.removeItem("activeTenantId");
              localStorage.removeItem("activeProduct");
              sessionStorage.removeItem("user");
              sessionStorage.clear();
              window.location.href = "/login";
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-rose-50 hover:bg-rose-100 hover:text-rose-700 text-rose-600 rounded-xl transition-all font-black text-[10px] shadow-sm uppercase tracking-wider active:scale-95"
          >
            Sign Out
          </button>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
